import { useEffect, useRef } from 'react';
import { AgricultureOutlined, FileUploadOutlined } from '@mui/icons-material';
import { FormCard } from '@practium/ui';
import type { PropertyPageMode } from '../../types';
import styles from './PropertyPhotoCard.module.css';

interface PropertyPhotoCardProps {
	mode: PropertyPageMode;
	file: File | null;
	onFileChange: (file: File | null) => void;
	entityName?: string;
	avatarUrl?: string;
	onEditRequest?: () => void;
}

export const PropertyPhotoCard = ({
	mode,
	file,
	onFileChange,
	entityName,
	avatarUrl,
	onEditRequest,
}: PropertyPhotoCardProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const shouldAutoOpenRef = useRef(false);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onFileChange(e.target.files?.[0] ?? null);
	};

	useEffect(() => {
		if (mode !== 'view' && shouldAutoOpenRef.current) {
			shouldAutoOpenRef.current = false;
			inputRef.current?.click();
		}
	}, [mode]);

	if (mode === 'view') {
		const canEditPhoto = !!onEditRequest;
		const handleViewClick = () => {
			if (!canEditPhoto) return;
			shouldAutoOpenRef.current = true;
			onEditRequest?.();
		};

		return (
			<div className={styles.sideCard}>
				<FormCard icon={<AgricultureOutlined />} title="Foto" className={styles.card}>
					<div
						className={`${styles.viewArea} ${avatarUrl ? styles.hasImage : ''} ${canEditPhoto ? styles.clickable : ''}`}
						onClick={canEditPhoto ? handleViewClick : undefined}
						role={canEditPhoto ? 'button' : undefined}
						tabIndex={canEditPhoto ? 0 : undefined}
						onKeyDown={canEditPhoto ? (e) => e.key === 'Enter' && handleViewClick() : undefined}
					>
						{avatarUrl ? (
							<div className={styles.previewWrapper}>
								<img src={avatarUrl} alt={entityName ?? 'Foto'} className={styles.previewImage} />
								{entityName && (
									<span className={styles.badge}>
										<span className={styles.badgeDot}>&#9679;</span>
										{entityName}
									</span>
								)}
							</div>
						) : (
							<>
								<AgricultureOutlined className={styles.placeholderIcon} />
								{entityName && (
									<span className={styles.badge}>
										<span className={styles.badgeDot}>&#9679;</span>
										{entityName}
									</span>
								)}
							</>
						)}
					</div>
				</FormCard>
			</div>
		);
	}

	const previewSrc = file ? URL.createObjectURL(file) : avatarUrl ?? null;
	const hasPreview = previewSrc !== null;

	return (
		<div className={styles.sideCard}>
			<FormCard icon={<AgricultureOutlined />} title="Foto" className={styles.card}>
				<div
					className={`${styles.uploadArea} ${hasPreview ? styles.hasImage : ''}`}
					onClick={() => inputRef.current?.click()}
					role="button"
					tabIndex={0}
					onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
				>
					{hasPreview ? (
						<div className={styles.previewWrapper}>
							<img src={previewSrc} alt="Preview" className={styles.previewImage} />
							<FileUploadOutlined className={styles.uploadIconOverlay} />
							<span className={styles.badge}>
								<span className={styles.badgeDot}>&#9679;</span>
								{file ? file.name : (entityName ?? '')}
							</span>
						</div>
					) : (
						<div className={styles.uploadContent}>
							<FileUploadOutlined className={styles.uploadIcon} />
						</div>
					)}
					<input
						ref={inputRef}
						type="file"
						accept="image/*"
						className={styles.hiddenInput}
						onChange={handleFileChange}
					/>
				</div>
			</FormCard>
		</div>
	);
};
